import type { ComposeBodyKind } from './types';
import { extractPlainTextFromHtml } from './htmlToPlainText';
import { effectivePlainTextBody } from '../utils/phoenixPlaceholderPlainText';
import { isPlainTextOpsTemplate } from './normalizePlainText';

export function isTrivialHtml(html: string): boolean {
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim().length === 0;
}

function plainTextMatchesHtmlContent(text: string, html: string): boolean {
  if (typeof document === 'undefined') return false;
  const doc = new DOMParser().parseFromString(`<div id="cmp">${html}</div>`, 'text/html');
  const root = doc.getElementById('cmp');
  const htmlText = (root?.textContent ?? '').replace(/\s+/g, ' ').trim();
  const plain = text.replace(/\s+/g, ' ').trim();
  if (!plain || !htmlText) return false;
  const ratio = plain.length / htmlText.length;
  return ratio > 0.82 && ratio < 1.18;
}

/** True when HTML carries compose/Slate formatting (not just unstyled paragraphs). */
export function htmlHasSlateRichFormatting(html: string): boolean {
  if (!html?.trim()) return false;
  if (/<(?:strong|b|em|i|u|s|strike|a|h[1-3]|ul|ol|li)\b/i.test(html)) return true;
  if (/<span[^>]*\sstyle\s*=/i.test(html)) return true;
  if (/<(?:p|div|font)[^>]*\sstyle\s*=[^>]*(?:color|font-family|font-size|background)/i.test(html)) {
    return true;
  }
  if (/<[^>]*\sstyle\s*=[^>]*font-weight\s*:\s*(?:bold|[6-9]00)\b/i.test(html)) return true;
  if (/<font[^>]*\b(?:color|face|size)\s*=/i.test(html)) return true;
  return false;
}

export function htmlHasRichContent(html: string): boolean {
  if (/<img\b/i.test(html)) return true;
  if (htmlHasSlateRichFormatting(html)) return true;
  if (!/<table\b/i.test(html)) return false;
  if (typeof document === 'undefined') return false;
  const doc = new DOMParser().parseFromString(`<div id="rich">${html}</div>`, 'text/html');
  const root = doc.getElementById('rich');
  if (!root) return false;
  for (const table of Array.from(root.querySelectorAll('table'))) {
    const rows = table.querySelectorAll('tr').length;
    const cells = table.querySelectorAll('td, th').length;
    if (rows <= 1 && cells <= 1) continue;
    if (rows === 1 && cells <= 4) continue;
    if (rows > 2 || cells > 8) return true;
  }
  return false;
}

function htmlLooksLikePlainTextReport(html: string): boolean {
  if (/<pre\b/i.test(html)) return false;
  if (typeof document === 'undefined') return false;
  const text = extractPlainTextFromHtml(html);
  // Phoenix PH2004 uses FONT/SPAN styles but content is a plain FM:/TO: report.
  if (isPlainTextOpsTemplate(text)) return true;
  if (htmlHasSlateRichFormatting(html)) return false;
  const doc = new DOMParser().parseFromString(`<div id="r">${html}</div>`, 'text/html');
  const root = doc.getElementById('r');
  if (!root) return false;
  const ps = root.querySelectorAll('p');
  if (ps.length >= 2) {
    const short = Array.from(ps).filter((p) => (p.textContent?.length ?? 0) <= 120).length;
    return short / ps.length >= 0.8;
  }
  const divs = root.querySelectorAll('div');
  if (divs.length >= 4 && ps.length === 0) {
    const short = Array.from(divs).filter((d) => (d.textContent?.length ?? 0) <= 120).length;
    return short / divs.length >= 0.85;
  }
  return false;
}

export function classifyBodyKind(
  bodyHtml: string | null | undefined,
  bodyText: string | null | undefined,
): ComposeBodyKind {
  const htmlStr = bodyHtml?.trim() ?? '';
  const text = effectivePlainTextBody(bodyText) ?? '';

  if (!text && htmlStr) {
    if (isTrivialHtml(htmlStr)) return 'plain';
    if (htmlLooksLikePlainTextReport(htmlStr)) return 'plain';
    if (/mso-|word\.document|<o:p/i.test(htmlStr) && !htmlHasRichContent(htmlStr)) return 'plain';
    return 'rich';
  }

  if (!text) return 'rich';
  if (!htmlStr || isTrivialHtml(htmlStr)) return 'plain';
  if (isPlainTextOpsTemplate(text)) return 'plain';
  if (htmlLooksLikePlainTextReport(htmlStr)) return 'plain';
  if (!text.includes('\n')) return 'rich';
  if (!htmlHasRichContent(htmlStr)) return 'plain';
  if (plainTextMatchesHtmlContent(text, htmlStr)) return 'plain';
  if (/mso-|word\.document|<o:p/i.test(htmlStr)) return 'plain';
  return 'rich';
}
